import type {
  ClientDuplicatesResult,
  ClientMergePreview,
  DuplicateMatchReason,
  MergeClientsRequest,
  MergePhoneMatchesResult,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"
import { newIdempotencyKey } from "./idempotency"

/**
 * Дубли и слияние карточек (3-А7) — право clients.merge.
 *
 * Слияние и отмена идут с ключом идемпотентности: повтор после обрыва связи обязан
 * вернуть тот же ответ, а не слить карточки второй раз с другим исходным состоянием.
 * Массовое слияние ключа не требует — его защищает число пар, которое человек подтвердил.
 */
export function fetchDuplicates(
  input: { readonly reason: DuplicateMatchReason | null; readonly cursor: string | null; readonly limit?: number },
  signal?: AbortSignal,
): Promise<ClientDuplicatesResult> {
  const params = new URLSearchParams({ limit: String(input.limit ?? 50) })
  if (input.reason !== null) params.set("reason", input.reason)
  if (input.cursor !== null) params.set("cursor", input.cursor)
  return apiRequest<ClientDuplicatesResult>(`/cabinet/clients/duplicates?${params.toString()}`, { signal })
}

export function fetchMergePreview(leftId: string, rightId: string, signal?: AbortSignal): Promise<ClientMergePreview> {
  const params = new URLSearchParams({ leftId, rightId })
  return apiRequest<ClientMergePreview>(`/cabinet/clients/merge-preview?${params.toString()}`, { signal })
}

export function mergeClients(request: MergeClientsRequest): Promise<{ readonly survivorId: string }> {
  return apiRequest<{ survivorId: string }>("/cabinet/clients/merge", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("client-merge"),
  })
}

export function unmergeClient(clientId: string): Promise<void> {
  return apiRequest("/cabinet/clients/unmerge", {
    method: "POST",
    body: { clientId },
    idempotencyKey: newIdempotencyKey("client-unmerge"),
  })
}

/** «Это разные люди» — пара больше не предлагается никогда. */
export function markClientsDistinct(clientIds: readonly [string, string], note?: string): Promise<void> {
  return apiRequest("/cabinet/clients/duplicates/distinct", {
    method: "POST",
    body: note === undefined ? { clientIds } : { clientIds, note },
  })
}

export function mergePhoneMatches(expectedPairs: number): Promise<MergePhoneMatchesResult> {
  return apiRequest<MergePhoneMatchesResult>("/cabinet/clients/merge-phone-matches", {
    method: "POST",
    body: { expectedPairs },
  })
}
